import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { BASE_URI } from "../constants/api";
import { RequestProps } from "../types/request";
import { RequestError } from "../errors/RequestError";

import useAuth from "./useAuth";
import { createIntlInstance } from "../i18n";
import { getLocaleFromPath, localizePath, resolveOfflineLocale } from "../i18n/locale-path";

function useRequest<ResponseType>() {
  const [error, setError] = useState<Error|RequestError|null>(null);
  const [response, setResponse] = useState<ResponseType|null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);

  const {token, removeAuth} = useAuth();

  const navigateTo = useNavigate();
  const location = useLocation();
  const locale =
    getLocaleFromPath(location.pathname) ?? resolveOfflineLocale();

  const request = useCallback(<T>(
      base: string,
      pathname: string,
      requestProps?: RequestProps,
      body?: T
    ) => {
      const intl = createIntlInstance(locale);
      const headers = new Headers([
        ["Content-Type", "application/json"]
      ]);
    
      if (requestProps?.secure) {
        if (!token) {
          const errorToken = new Error('Required token');
          setError(errorToken);
          removeAuth();
          return;
        }
        headers.set('Authorization', 'Bearer ' + token);
      }
  
      const method = requestProps?.method ?? 'GET';
      const searchParams = requestProps?.searchParams;
  
      setLoading(true);
      setError(null);
      fetch(`${BASE_URI}/${base}/${pathname}${searchParams ? '?' + new URLSearchParams(searchParams) : ''}`, {
        headers,
        method,
        body: JSON.stringify(body),
        mode: "cors"
      })
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        }
        if (res.status === 403 && location.pathname.includes('dashboard') && !location.pathname.includes('feature/add')) {
          removeAuth();
          return navigateTo(localizePath(locale, '/auth/login'), {state: location.pathname});
        }
        return res.text().then(text => {
          const error = JSON.parse(text);
          throw new RequestError(
            error.message ?? intl.formatMessage({ id: 'common.errorOccurred' }),
            res.status,
            error.data
          );
        });
      })
      .then((data: ResponseType) =>  setResponse(Object.assign({}, data)))
      .catch((error) => {
        if (error instanceof RequestError) {
          setError(error);
        } else {
          setError(new Error(intl.formatMessage({ id: 'common.serviceUnavailableRetry' })));
          console.error('++ Request error details: ', error);
        }
        setResponse(null);
      })
      .finally(() => setLoading(false))
  }, [token, removeAuth, navigateTo, location.pathname, locale])

  const updateResponse = useCallback((data: ResponseType) => {
    setResponse(() => ({...data}));
  }, [])

  return {isLoading, error, response, request, updateResponse}
}

export default useRequest;